import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { PERMISSION_PUNCH_REPORT } from "../../constants/api";
import { EmployeeDetails , ContainerCard } from "../../components/ui/cards";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";

const ALERT_BADGE = {
    OK: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
};

const PermissionPunchReport = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [approvalStatus, setApprovalStatus] = useState("all");
    const navigate = useNavigate();

    const columns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "260px",
            Cell: ({ row }) => {
                const empDetails = {
                    ...row?.employee,
                    designation_id: row?.employee?.designation
                        ? { id: row.employee.designation.id, designation_name: row.employee.designation.name }
                        : { id: 0, designation_name: "" },
                    first_name: row?.employee_name || "-",
                    id: row?.employee_code || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "permission_date",
            Header: "Date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        {
            accessor: "permission_from",
            Header: "Perm. from",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "permission_to",
            Header: "Perm. to",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "approval_status",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "check_in",
            Header: "Check in",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("h:mm A") : value ?? "-",
        },
        {
            accessor: "check_out",
            Header: "Check out",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("h:mm A") : value ?? "-",
        },
        {
            accessor: "punch_alert",
            Header: "Punch alert",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "text-xs font-medium px-2 py-0.5 rounded",
                        value === "OK"
                            ? ALERT_BADGE.OK
                            : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                    )}
                >
                    {value ?? "-"}
                </span>
            ),
        },
        {
            accessor: "reason",
            Header: "Reason",
            Cell: ({ value }) => (
                <span className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{value ?? "-"}</span>
            ),
        },
    ];

    return (
        <ContainerCard className="font-sans">
            <div className="flex justify-between items-center mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => navigate("/reports")} className="cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Permission Punch Report ({totalRecords})
                    </h1>
                </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Permission windows compared with actual attendance punches and approval status.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { value: "all", label: "All statuses" },
                    { value: "Approved", label: "Approved" },
                    { value: "pending", label: "Pending" },
                    { value: "Rejected", label: "Rejected" },
                ].map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setApprovalStatus(opt.value)}
                        className={cn(
                            "px-3 py-1.5 text-xs rounded-lg border transition-colors",
                            approvalStatus === opt.value
                                ? "bg-sidebar text-white border-sidebar"
                                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={PERMISSION_PUNCH_REPORT}
                serverSide={true}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
                filter={{ approval_status: approvalStatus === "all" ? "" : approvalStatus }}
                printFilter={{ approval_status: approvalStatus === "all" ? "" : approvalStatus }}
            />
        </ContainerCard>
    );
}

export default PermissionPunchReport;
